import { getRegionByPublicRegion } from '../../../utils/appConfig';

export type ConnectionDirection = 'inbound' | 'outbound';

export type StatusIndicatorType =
  | 'error'
  | 'warning'
  | 'success'
  | 'info'
  | 'stopped'
  | 'pending'
  | 'in-progress'
  | 'loading';

export interface StatusDisplay {
  type: StatusIndicatorType;
  label: string;
}

export interface CrossClusterDomainInfo {
  ownerId?: string | null;
  domainName?: string | null;
  region?: string | null;
}

export interface CrossClusterConnection {
  connectionId?: string | null;
  connectionAlias?: string | null;
  connectionStatus?: string | null;
  connectionMode?: string | null;
  localDomainInfo?: CrossClusterDomainInfo | null;
  remoteDomainInfo?: CrossClusterDomainInfo | null;
}

export interface DisplayConnection {
  connectionId: string;
  connectionAlias: string;
  status: string;
  direction: ConnectionDirection;
  remoteDomainName: string;
  remoteAccountId: string;
  remoteRegion: string;
  connectionMode: string;
  tumblerLink: string | null;
}

// Whether the domain has any follower-side replication (used by the Index-Level
// Replication table to pick its empty state / show the summary).
export const hasFollowerReplicationActivity = (followerStats: any): boolean => {
  if (!followerStats || typeof followerStats !== 'object') return false;
  const num = (k: string) =>
    typeof followerStats[k] === 'number' ? followerStats[k] : 0;
  return (
    num('num_syncing_indices') +
      num('num_paused_indices') +
      num('num_failed_indices') +
      num('num_bootstrapping_indices') >
    0
  );
};

const TUMBLER_LINK_PARTITIONS = new Set(['aws', 'aws-us-gov']);

export const buildTumblerLink = (
  remoteRegion?: string | null,
  remoteAccountId?: string | null,
  remoteDomainName?: string | null,
  stage?: string | null
): string | null => {
  if (!remoteRegion || !remoteAccountId || !remoteDomainName || !stage) return null;
  const entry = getRegionByPublicRegion(remoteRegion);
  if (!entry) return null;
  if (entry.partition && !TUMBLER_LINK_PARTITIONS.has(entry.partition)) return null;
  const airportCode = entry.airportCode.toLowerCase();
  return `https://${airportCode}.${stage}.tumbler.oss.aws.dev/${remoteAccountId}/${remoteDomainName}`;
};

const STATUS_MAP: Record<string, StatusDisplay> = {
  ACTIVE: { type: 'success', label: 'Active' },
  PENDING_ACCEPTANCE: { type: 'pending', label: 'Pending Acceptance' },
  APPROVED: { type: 'in-progress', label: 'Approved' },
  PROVISIONING: { type: 'in-progress', label: 'Provisioning' },
  VALIDATING: { type: 'loading', label: 'Validating' },
  VALIDATION_FAILED: { type: 'error', label: 'Validation Failed' },
  REJECTED: { type: 'error', label: 'Rejected' },
  DELETING: { type: 'warning', label: 'Deleting' },
  DELETED: { type: 'stopped', label: 'Deleted' },
  CONNECTED: { type: 'success', label: 'Connected' },
  NOT_CONNECTED: { type: 'error', label: 'Not Connected' },
};

export const mapConnectionStatus = (statusCode?: string | null): StatusDisplay => {
  const key = (statusCode || '').toUpperCase();
  return STATUS_MAP[key] || { type: 'info', label: statusCode || 'Unknown' };
};

export const transformConnections = (
  outbound: CrossClusterConnection[] = [],
  inbound: CrossClusterConnection[] = [],
  stage?: string | null
): DisplayConnection[] => {
  const toDisplay = (
    conn: CrossClusterConnection,
    direction: ConnectionDirection
  ): DisplayConnection => {
    const remote = conn.remoteDomainInfo || {};
    const remoteRegion = remote.region || '';
    const remoteAccountId = remote.ownerId || '';
    const remoteDomainName = remote.domainName || '';
    return {
      connectionId: conn.connectionId || '',
      connectionAlias: conn.connectionAlias || '',
      status: conn.connectionStatus || '',
      direction,
      remoteDomainName,
      remoteAccountId,
      remoteRegion,
      connectionMode: conn.connectionMode || '',
      tumblerLink: buildTumblerLink(remoteRegion, remoteAccountId, remoteDomainName, stage),
    };
  };

  return [
    ...outbound.map((c) => toDisplay(c, 'outbound')),
    ...inbound.map((c) => toDisplay(c, 'inbound')),
  ];
};
